import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabasePublic, getSupabase, loadSupabaseSchema } from '../../supabaseClient';
import { useToast } from '@chakra-ui/react';
import { isAllowedRole } from '../../../config/roles';

const AuthCallback = () => {
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        // Extract tokens from URL (query params or hash)
        const url = new URL(window.location.href);
        console.log("🔁 [AUTH CALLBACK] Landed on callback", {
          fullHref: window.location.href,
          origin: window.location.origin,
          hash: window.location.hash,
          search: window.location.search,
        });
        const params = new URLSearchParams(
          window.location.hash.substring(1) || url.search
        );
        const accessToken = params.get('access_token');
        const refreshToken = params.get('refresh_token');
        const errorParam = params.get('error');
        const errorDescription = params.get('error_description');
        console.log("🔁 [AUTH CALLBACK] URL params", {
          hasAccessToken: !!accessToken,
          hasRefreshToken: !!refreshToken,
          error: errorParam,
          errorDescription,
          allParams: Object.fromEntries(params.entries()),
        });

        if (!accessToken || !refreshToken) {
          throw new Error('Missing access_token or refresh_token');
        }

        // Set session on public client
        const { error: sessionError } = await supabasePublic().auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });

        if (sessionError) {
          throw new Error(`Error setting session: ${sessionError.message}`);
        }

        // Get dynamic schema client
        const supabase = await getSupabase();

        // Set session on schema-specific client
        const { data: { user }, error: schemaSessionError } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });

        if (schemaSessionError || !user) {
          throw new Error(`Error setting schema session: ${schemaSessionError?.message}`);
        }

        // Store user info
        localStorage.setItem('user_id', user.id);
        localStorage.setItem('access_token', accessToken);
        localStorage.setItem('refresh_token', refreshToken);

        const isGoogleUser = user.app_metadata?.provider === 'google';

        // Check if user exists in tenant schema (or public if no schema set yet)
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('role')
          .eq('email', user.email);

        if (userError) {
          if (userError.code === 'PGRST116' || userError.message.includes('permission denied')) {
            // No user found or permission denied, proceed to insert
            console.log('User not found in tenant schema or permission denied, proceeding to insert');
          } else {
            throw new Error(`Error fetching user data: ${userError.message}`);
          }
        }

        if (userData?.length > 0) {
          // User exists — allow access only if role is in allow-list
          const role = userData[0]?.role;
          if (isAllowedRole(role)) {
            navigate('/schedules');
            return;
          }
          // No allowed role: route Google users based on whether they picked an account type yet.
          // Role with NO comma (e.g. 'user') → haven't completed signup form yet.
          // Role WITH comma (e.g. 'user,doctor') → completed form, waiting for admin approval.
          if (isGoogleUser) {
            if (role && role.includes(',')) {
              // Strip the 'user,' prefix and save the cleaner account type
              const stripped = role.split(',').slice(1).join(',').trim();
              await supabasePublic().from('users').update({ role: stripped }).eq('email', user.email);
              console.log('Google user completed signup, pending admin approval. Role stripped to:', stripped);
              navigate('/403', { replace: true });
            } else {
              console.log('Google user has no account type yet, redirecting to complete signup');
              navigate('/auth/complete-signup', { replace: true });
            }
            return;
          }
          navigate('/403');
          return;
        }

        // No tenant user — check public.users
        const { data: publicUserData, error: publicUserError } = await supabasePublic()
          .from('users')
          .select('role')
          .eq('email', user.email)
          .maybeSingle();

        const noPublicUser = publicUserError?.code === 'PGRST116' || !publicUserData;

        if (isGoogleUser && !isAllowedRole(publicUserData?.role)) {
          const publicRole = publicUserData?.role || '';
          if (!noPublicUser && publicRole.includes(',')) {
            // User completed the signup form (role has comma), waiting for admin approval.
            const stripped = publicRole.split(',').slice(1).join(',').trim();
            await supabasePublic().from('users').update({ role: stripped }).eq('email', user.email);
            console.log('Google user completed signup, pending admin approval. Role stripped to:', stripped);
            navigate('/403', { replace: true });
          } else {
            // No row yet, or row with no comma (just 'user') — needs to complete signup form.
            console.log('Google user needs to complete signup (role/reason)');
            navigate('/auth/complete-signup', { replace: true });
          }
          return;
        }

        if (publicUserError && publicUserError.code !== 'PGRST116') {
          throw new Error(`Error fetching public user data: ${publicUserError.message}`);
        }

        // Non-Google user with no public record: create placeholder and send to login
        if (noPublicUser) {
          const { error: publicInsertError } = await supabasePublic()
            .from('users')
            .insert([{
              id: user.id,
              email: user.email,
              name: user.user_metadata?.username || user.user_metadata?.full_name || user.email.split('@')[0],
              role: null
            }]);
          if (publicInsertError) {
            throw new Error(`Error creating user in public schema: ${publicInsertError.message}`);
          }
          alert("Welcome!\n\nYour account has been created. Please wait for an admin to grant you access.");
          navigate('/login');
          return;
        }

        // User exists in public with allowed role but not in tenant — insert tenant user
        const role = publicUserData?.role || 'user';
        const { error: insertError } = await supabase
          .from('users')
          .upsert({
            id: user.id,
            name: user.user_metadata?.username || user.user_metadata?.full_name || 'Anonymous',
            email: user.email,
            role,
          });

        if (insertError) {
          throw new Error(`Error inserting user: ${insertError.message}`);
        }

        console.log('User registered in tenant schema');
        if (isAllowedRole(role)) {
          navigate('/schedules');
        } else {
          navigate('/403');
        }
      } catch (error) {
        console.error('Auth callback error:', error.message);
        toast({
          title: "Authentication failed",
          description: error.message,
          status: "error",
          duration: 8000,
          isClosable: true,
          position: "top"
        });
        navigate('/login'); // Redirect to login on error
      } finally {
        setIsLoading(false);
      }
    };

    handleAuthCallback();
  }, [navigate, toast]);

  return <div>{isLoading ? 'Loading...' : 'Processing...'}</div>;
};

export default AuthCallback;